import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { formatMoney, invoiceStatusLabel } from '../../lib/hostInvoice';

interface InvoiceRow {
  id: string;
  number: string;
  currency: string;
  total_cents: number;
  status: string;
  invoice_number: string;
  invoice_date: string;
  invoice_file_path: string;
  submitted_at: string;
  rejected_reason: string | null;
  paid_at: string | null;
  payment_reference: string | null;
  host_invoice_items: { count: number }[];
}

const TONE: Record<'wait' | 'good' | 'bad', string> = {
  wait: 'text-app-muted',
  good: 'text-emerald-500',
  bad: 'text-crimson',
};

/** The invoices a host has sent, newest first, with where each one stands. */
export function HostInvoicesList({ hostId }: { hostId: string }) {
  const [rows, setRows] = useState<InvoiceRow[] | null>(null);
  const [fileError, setFileError] = useState(false);

  useEffect(() => {
    void supabase
      .from('host_invoices')
      .select('id, number, currency, total_cents, status, invoice_number, invoice_date, invoice_file_path, submitted_at, rejected_reason, paid_at, payment_reference, host_invoice_items(count)')
      .eq('host_id', hostId)
      .order('submitted_at', { ascending: false })
      .then(({ data }) => setRows((data as unknown as InvoiceRow[]) ?? []));
  }, [hostId]);

  async function openFile(path: string) {
    setFileError(false);
    const { data, error } = await supabase.storage.from('host-invoices').createSignedUrl(path, 60);
    if (error || !data?.signedUrl) { setFileError(true); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  }

  if (!rows || rows.length === 0) return null;

  return (
    <section className="mb-6" aria-label="Your invoices">
      <h2 className="text-sm font-semibold text-app-fg mb-2">Your invoices</h2>
      <div className="flex flex-col gap-2">
        {rows.map((inv) => {
          const status = invoiceStatusLabel(inv);
          const trips = inv.host_invoice_items?.[0]?.count ?? 0;
          return (
            <div key={inv.id} className="bg-app-card rounded-xl border border-app-border p-4 flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-app-fg">
                  {inv.invoice_number}
                  <span className="text-app-muted font-normal"> · {new Date(inv.invoice_date).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </p>
                <p className="text-xs text-app-muted">{inv.number} · {trips} trip{trips === 1 ? '' : 's'}</p>
                <p className={`text-xs mt-1 ${TONE[status.tone]}`}>{status.label}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-app-fg">{formatMoney(inv.total_cents, inv.currency)}</p>
                <button type="button" onClick={() => void openFile(inv.invoice_file_path)} className="text-xs text-gold underline underline-offset-2">
                  View file
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {fileError ? <p role="alert" className="text-xs text-crimson mt-2">We couldn’t open that file. Please try again.</p> : null}
    </section>
  );
}

export default HostInvoicesList;
